import { Controller } from '@hotwired/stimulus'
import { readInheritableStaticValue } from 'util/class'

class VueController extends Controller {
  static vueComponent = ''
  static targets = ['defaultSlot']

  async connect () {
    await this.loadVueComponent()
  }

  async disconnect () {
    await this.unloadVueComponent()
  }

  get componentKey () {
    return readInheritableStaticValue(this.constructor, 'vueComponent')
  }

  get tagName () {
    return 'div'
  }

  get vueTarget () {
    if (this.componentRoot) {
      return this.componentRoot
    }

    return this.element
  }

  // async load in vue js
  async loadVueComponent () {
    const {
      createApp,
      provide,
      h,
      defineAsyncComponent,
      reactive
    } = await import('vue')

    // Dynamically import the Vue component
    const Component = defineAsyncComponent(() => import(`../components/${this.componentKey}.vue`))

    const handler = (eventName, data) => this.#handler(eventName, data)

    const reactiveProps = reactive(Object.assign({}, this.props))

    // Capture slot content if it exists
    let slotContent = null
    if (this.hasDefaultSlotTarget) {
      slotContent = this.defaultSlotTarget.innerHTML
    }

    const app = createApp({
      setup () {
        provide('handle', handler)
      },

      render: () => {
        // If there's slot content, pass it as the default slot
        if (slotContent) {
          return h(Component, reactiveProps, {
            default: () => h('div', { innerHTML: slotContent })
          })
        }

        return h(Component, reactiveProps)
      }
    })

    this.reactiveProps = reactiveProps

    this.vueTarget.innerHTML = ''

    const element = document.createElement(this.tagName)
    this.vueTarget.appendChild(element)

    app.mount(this.vueTarget)

    this.instance = app
  }

  async unloadVueComponent () {
    if (this.instance) {
      this.instance.unmount()
      this.instance = null
    }
  }

  get props () {
    return {}
  }

  handleEvent (eventName, data) {
    // override in child classes
  }

  update (newProps) {
    for (const [key, value] of Object.entries(newProps)) {
      this.reactiveProps[key] = value
    }
  }

  #handler (eventName, data) {
    switch (eventName) {
      case 'value-changed':
        this.#handleValueChangedfromVue(data)
        break
      default:
        this.handleEvent(eventName, data)
        break
    }
  }

  #handleValueChangedfromVue (data) {
    // iterate over all keys and values of data and set them to the controller
    for (const [key, value] of Object.entries(data)) {
      this[`${key}Value`] = value
    }
  }
}

export {
  VueController
}
